import type { ReviewRemark, WorkReview } from '@ndrc/app-sdk/client';
import type { ActivityCheckResult, CheckFinding, ContactsReviewResult } from '../lib/queries';
import { reviewScopeOf } from '../lib/reviewScope';
import { callCore, mutateCore } from '../lib/rpc';
import { t } from '../messages';

/**
 * **L'implémentation de l'unique capacité qu'une app peut demander au cœur : la relecture**
 * (`AppContext.review()`, `@ndrc/app-sdk/client` ; ADR-014).
 *
 * Elle vit ICI, dans l'hôte d'application, et nulle part ailleurs : c'est le bureau qui monte une
 * app, c'est donc lui qui lui tend cette fonction déjà refermée sur la classe de l'élève connecté.
 * L'app, elle, reçoit une fonction **sans paramètre** — il n'y a aucun endroit où elle pourrait
 * nommer une autre opération, un autre élève ou un autre espace de travail.
 *
 * **Deux appels au cœur, dans cet ordre, et l'ordre compte** — c'est celui de l'écran d'activité
 * (`apps/desktop/src/activity/ActivityBoard.tsx`), repris à l'identique plutôt que réinventé :
 *
 * 1. `activities.check` — la vérification de la FORME. Gratuite, immédiate, sûre. Ses constats
 *    (« cette adresse n'a pas de @ ») sont vrais pour tout le monde, toujours.
 * 2. `contacts.review` — la relecture PAR JUGEMENT. Elle appelle un modèle de langue, coûte de
 *    l'argent, et se trompe parfois. C'est le cœur qui parle à la passerelle
 *    (`@ndrc/ai-gateway`), jamais l'app : une app ne peut même pas importer ce paquet
 *    (`no-core-internals`, `.dependency-cruiser.cjs`).
 *
 * **Rien de ce que l'app envoie ne désigne la cible.** Aucune des deux opérations ne reçoit de
 * `workspaceId` : le cœur le résout depuis la session (`resolveDefaultWorkspace`,
 * `@ndrc/core-domain`). `classId` est celui de la classe où l'élève est déjà connecté — il vient
 * du bureau, jamais de l'app — et il ne sert qu'à lever l'ambiguïté d'un enseignant qui enseigne
 * plusieurs classes. Un élève qui glisserait `userId` dans la requête ne changerait rien :
 * `contacts.review` n'honore ce champ que pour un enseignant (voir son gestionnaire).
 *
 * **Une seule chose peut « échouer à moitié », et c'est voulu** : si la partie jugement est
 * refusée (aucune clé sur la plateforme, plafond de dépense du jour atteint, usage coupé par
 * l'enseignant, fournisseur en panne), la partie forme est rendue quand même, et le refus arrive
 * dans `doubtfulUnavailable` avec la phrase française écrite par le cœur. Un élève ne doit jamais
 * se retrouver devant un bouton mort parce qu'une limite budgétaire a été atteinte à l'autre bout
 * de l'établissement.
 */

function toRemark(finding: CheckFinding): ReviewRemark {
  return {
    subjectId: finding.subjectId,
    subjectLabel: finding.subjectLabel,
    field: finding.field,
    message: finding.message,
    severity: finding.severity,
  };
}

export async function reviewWork(
  classId: string | undefined,
  /**
   * L'élève dont l'enseignant lit le travail, ou `null`. **L'app ne le fournit pas et ne peut pas
   * le fournir** : elle appelle toujours `ctx.review()`, sans paramètre. C'est l'hôte qui referme,
   * ici, comme il referme déjà `classId` — la garantie de l'ADR-014 (une app ne peut désigner ni
   * un camarade, ni un autre espace) n'a pas bougé d'un caractère.
   *
   * Les deux opérations honorent `userId` **pour un enseignant seulement**, et chacune revérifie
   * tout côté serveur (classe réellement enseignée, appartenance de l'élève à cette classe) :
   * `resolveObservedWorkspace`, `@ndrc/core-domain`. Un élève qui glisserait ce champ dans la
   * requête ne changerait rien — sa relecture porterait toujours sur son propre espace.
   */
  observing: { classId: string; userId: string } | null,
  missionRunId?: string,
  sourceAppId?: string,
): Promise<WorkReview> {
  const scope =
    observing !== null
      ? { classId: observing.classId, userId: observing.userId }
      : { ...(classId ? { classId } : {}), ...(missionRunId ? { missionRunId } : {}) };

  // La vérification gratuite d'abord : elle ne peut rien coûter, et ses constats sont ceux dont
  // l'élève a besoin même quand la relecture par jugement est indisponible.
  const check = await callCore<ActivityCheckResult | null>('activities.check', scope);

  // `null` = aucun espace de travail à relire (le contrat de l'opération, jamais une erreur). On
  // s'arrête ICI, avant la relecture payante : on ne demande jamais à un modèle de relire un
  // fichier qui n'existe pas. Le message est celui que l'écran d'activité affiche déjà dans ce
  // cas — jamais une seconde rédaction du même refus.
  if (check === null) {
    throw new Error(`${t('activity.checkNoWorkspaceTitle')} ${t('activity.checkNoWorkspaceHint')}`);
  }

  const judgment = await mutateCore<ContactsReviewResult>(
    'contacts.review',
    scope,
    undefined,
    sourceAppId,
  );
  const wrong = check.findings.map(toRemark);
  // Une seule source pour les deux listes : la paire cohérente que le serveur a déjà partitionnée,
  // choisie et jamais concaténée (`lib/reviewScope.ts`). La version précédente collait
  // `check.notVerified` (état « le jugement n'a pas eu lieu ») à la moitié jugement de la relecture
  // : le panneau annonçait alors la MÊME question relue d'un côté et pas relue de l'autre.
  const { looked, notLooked } = reviewScopeOf(check, judgment);

  if (judgment.status === 'unavailable') {
    return {
      reviewed: check.scannedContacts,
      wrong,
      doubtful: [],
      looked,
      notLooked,
      doubtfulUnavailable: judgment.message,
    };
  }

  return {
    reviewed: check.scannedContacts,
    wrong,
    doubtful: judgment.findings.map(toRemark),
    // `notLooked` reste affiché même quand le jugement a bien tourné : un modèle de langue ne sait
    // toujours pas si une fiche MANQUE au fichier, ni si une information est vraie — ne jamais
    // laisser croire « tout est bon ». Ce qu'il vient de regarder, lui, est passé de l'autre côté.
    looked,
    notLooked,
    doubtfulUnavailable: null,
  };
}
